// Onboarding.tsx — the golden-path wizard (spec D6, M5 task-5, handoff §6), route
// `/onboarding?step=1..5&botId=...`. URL-driven so a refresh mid-wizard resumes exactly
// where the visitor left off (contract: "refresh-safe"). Two bounce rules keep the URL
// honest: `step > 1` with no `botId` at all, or a `botId` that 404s on fetch, both snap back
// to step 1 (`replace: true` so the bad URL never lands in history). This page owns every
// query/mutation for the wizard — a source-polling query shared by the Crawl and Try-it
// steps (mirrors `BotSources.tsx`'s 3s-while-syncing pattern) and a resync mutation for
// Crawl's Retry button — the step components themselves are presentational, taking data and
// callbacks as props (mirrors the plan's "one file per step" component split).
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import type { BotDto, SourceDto, SourceStatus } from '@repo/shared-types';
import { CrawlStep } from '../components/onboarding/CrawlStep';
import { GoLiveStep } from '../components/onboarding/GoLiveStep';
import { LookStep } from '../components/onboarding/LookStep';
import { Stepper, type StepId } from '../components/onboarding/Stepper';
import { TryItStep } from '../components/onboarding/TryItStep';
import { WebsiteStep } from '../components/onboarding/WebsiteStep';
import { apiFetch } from '../lib/api';
import { useT } from '../locales/context';

const SYNCING_STATUSES: SourceStatus[] = ['pending', 'crawling', 'indexing'];

function parseStep(raw: string | null): StepId {
  const n = Number(raw);
  return n === 2 || n === 3 || n === 4 || n === 5 ? (n as StepId) : 1;
}

/** Sized to a step card's real header + body while `bot` (needed by steps 4/5) is still in
 * flight — the same bordered-card shape every step already renders into (handoff Shell
 * Anatomy §06), not a bare loading label. */
function StepSkeleton() {
  return (
    <div className="flex flex-col gap-5 rounded-[14px] border bg-card p-8 shadow-el-1">
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-7 w-2/3" />
        <SkeletonText className="w-full" />
      </div>
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

function safeHostname(uri: string): string {
  try {
    return new URL(uri).hostname;
  } catch {
    return uri;
  }
}

export function Onboarding() {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [retryError, setRetryError] = useState<string | null>(null);
  // prod-copy-fixes cycle, defect 3 — separate from `retryError` (Retry and "Try a different
  // address" are two independent actions on the same error card; a failure in one must not
  // clobber or hide a stale message from the other).
  const [replaceUrlError, setReplaceUrlError] = useState<string | null>(null);

  const requestedStep = parseStep(searchParams.get('step'));
  const botId = searchParams.get('botId') ?? undefined;

  const { data: bot, error: botError } = useQuery({
    queryKey: ['bots', botId],
    queryFn: () => apiFetch<BotDto>(`/api/bots/${botId}`),
    enabled: !!botId,
    retry: false,
  });

  // Contract: direct entry to a later step without a botId, or a botId that 404s, bounces
  // to step 1 rather than rendering a step that has nothing to show.
  const bounceToStep1 = requestedStep > 1 && (!botId || !!botError);

  useEffect(() => {
    if (bounceToStep1) setSearchParams({ step: '1' }, { replace: true });
  }, [bounceToStep1, setSearchParams]);

  const step: StepId = bounceToStep1 ? 1 : requestedStep;

  const {
    data: sources,
    isPending: sourcesPending,
    error: sourcesError,
  } = useQuery({
    queryKey: ['bots', botId, 'sources'],
    queryFn: () => apiFetch<SourceDto[]>(`/api/bots/${botId}/sources`),
    enabled: !!botId && step >= 2,
    refetchInterval: (query) => {
      const list = query.state.data;
      return list?.some((s) => SYNCING_STATUSES.includes(s.status)) ? 3000 : false;
    },
  });

  // Onboarding only ever creates one source (step 1's url source) — the first row is it.
  const source = sources?.[0];
  const pageCount = (sources ?? []).reduce((sum, s) => sum + s.pageCount, 0);

  const resyncMutation = useMutation({
    mutationFn: (sourceId: string) =>
      apiFetch<SourceDto>(`/api/bots/${botId}/sources/${sourceId}/resync`, { method: 'POST' }),
    onSuccess: async () => {
      setRetryError(null);
      await queryClient.invalidateQueries({ queryKey: ['bots', botId, 'sources'] });
    },
    onError: (err: Error) => setRetryError(err.message),
  });

  // prod-copy-fixes cycle, defect 3 — "Try a different address" on a failed crawl. Deliberately
  // NOT routed through the Stepper's step 1: `WebsiteStep`'s submit creates a NEW bot, and
  // `PLAN_LIMITS.free.maxBots` is 1, so sending a Free visitor back there to fix a typo would
  // walk them into a bot-limit error instead of fixing anything. This replaces the source on
  // the SAME bot instead — delete the failed one, then create a fresh one with the corrected
  // URL. The sources list is ordered newest-first (`orderBy: { createdAt: 'desc' }`,
  // `packages/db/src/repos/sources.ts`) and `source` below is always `sources?.[0]`, so the
  // newly created source becomes the one CrawlStep polls automatically — no extra wiring needed
  // once the query invalidates.
  const replaceUrlMutation = useMutation({
    mutationFn: async (uri: string) => {
      if (!botId || !source) throw new Error(t.onboarding.noSourceToReplace);
      await apiFetch(`/api/bots/${botId}/sources/${source.id}`, { method: 'DELETE' });
      return apiFetch<SourceDto>(`/api/bots/${botId}/sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'url', uri }),
      });
    },
    onSuccess: async () => {
      setReplaceUrlError(null);
      setRetryError(null);
      await queryClient.invalidateQueries({ queryKey: ['bots', botId, 'sources'] });
    },
    onError: (err: Error) => setReplaceUrlError(err.message),
  });

  function goToStep(next: StepId, opts?: { botId?: string }) {
    const params: Record<string, string> = { step: String(next) };
    const effectiveBotId = opts?.botId ?? botId;
    if (effectiveBotId) params.botId = effectiveBotId;
    setSearchParams(params);
  }

  return (
    // Step 4 is wider from `lg` up, and only step 4. The widget panel it previews is 380px
    // (`apps/widget/src/styles.ts`), so inside a 680px card the two-column layout squeezed it
    // to 240px — the owner was picking a brand colour against a widget 63% of the shape their
    // visitors actually see, which makes the preview worse than useless. 860px fits a real
    // 380px preview beside the form; every other step keeps the narrower, calmer column.
    <div
      className={`mx-auto flex w-full flex-col gap-6 px-4 py-12 sm:px-6 ${
        step === 4 ? 'max-w-[680px] lg:max-w-[860px]' : 'max-w-[680px]'
      }`}
    >
      <div className="flex justify-end">
        {/* Task 7 (dashboard-shell cycle): was `/?stay=1` — `/bots` is now its own path, so
            the empty list there is reachable directly with no escape-hatch query param. */}
        <Link
          to="/bots"
          className="text-xs text-muted-foreground hover:text-foreground hover:underline"
        >
          {t.onboarding.skipForNow}
        </Link>
      </div>

      <Stepper current={step} onNavigate={(s) => goToStep(s)} />

      {step === 1 && <WebsiteStep onCreated={(newBotId) => goToStep(2, { botId: newBotId })} />}

      {step === 2 && botId && (
        <CrawlStep
          hostname={source ? safeHostname(source.uri) : (bot?.name ?? t.onboarding.crawlDefaultHostname)}
          source={source}
          sourcesPending={sourcesPending}
          sourcesError={sourcesError}
          onRetry={() => source && resyncMutation.mutate(source.id)}
          retrying={resyncMutation.isPending}
          retryError={retryError}
          onReplaceUrl={(uri) => replaceUrlMutation.mutate(uri)}
          replacingUrl={replaceUrlMutation.isPending}
          replaceUrlError={replaceUrlError}
          onNext={() => goToStep(3)}
        />
      )}

      {step === 3 && botId && (
        <TryItStep botId={botId} pageCount={pageCount} onNext={() => goToStep(4)} />
      )}

      {step === 4 && botId && bot && (
        <LookStep botId={botId} bot={bot} onNext={() => goToStep(5)} />
      )}
      {step === 4 && botId && !bot && !botError && <StepSkeleton />}

      {step === 5 && botId && bot && (
        <GoLiveStep
          bot={bot}
          onGoToDashboard={() => navigate(`/bots/${botId}`)}
          onViewSources={() => navigate(`/bots/${botId}/sources`)}
        />
      )}
      {step === 5 && botId && !bot && !botError && <StepSkeleton />}
    </div>
  );
}
